import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { Hammer, ChevronRight, Sparkles, AlertTriangle, CheckCircle2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import AiAnalysisCard from "@/components/AiAnalysisCard";

const DISCIPLINES = [
  { id: "leisure", nl: "Recreatief / weidegang", en: "Leisure / pasture", base: 8 },
  { id: "dressage", nl: "Dressuur / lichte arena", en: "Dressage / light arena", base: 7 },
  { id: "jumping", nl: "Springen / wedstrijdsport", en: "Jumping / competition", base: 6 },
  { id: "eventing", nl: "Eventing / cross-country", en: "Eventing / cross-country", base: 5 },
  { id: "endurance", nl: "Endurance / lange ritten", en: "Endurance / long rides", base: 5 },
  { id: "racing", nl: "Renpaard / Western reining", en: "Racing / Western reining", base: 5 },
];

const SURFACES = [
  { id: "soft", nl: "Zacht (zand, weide)", en: "Soft (sand, pasture)", delta: 1 },
  { id: "mixed", nl: "Gemengd (binnen + buiten)", en: "Mixed (indoor + outdoor)", delta: 0 },
  { id: "hard", nl: "Hard (asfalt, harde paden)", en: "Hard (asphalt, hard trails)", delta: -1 },
  { id: "rocky", nl: "Rotsachtig / bergachtig", en: "Rocky / mountainous", delta: -2 },
];

const HOOF_QUALITIES = [
  { id: "excellent", nl: "Sterk, gezond, snel groeiend", en: "Strong, healthy, fast growing", delta: 1 },
  { id: "good", nl: "Goed, normale groei", en: "Good, normal growth", delta: 0 },
  { id: "fair", nl: "Brokkelig, langzame groei", en: "Brittle, slow growth", delta: -1 },
  { id: "poor", nl: "Zwak (laminitis-geschiedenis)", en: "Weak (laminitis history)", delta: -2 },
];

const FarrierScheduleCalculatorPage = () => {
  const { lang } = useLang();
  const isNl = lang === "nl";
  const [discipline, setDiscipline] = useState("dressage");
  const [surface, setSurface] = useState("mixed");
  const [hoof, setHoof] = useState("good");
  const [shod, setShod] = useState(false);

  useEffect(() => {
    document.title = isNl
      ? "Hoefsmid Planner — optimaal bekap- en beslag-interval | HorseAppCompare"
      : "Farrier Schedule Calculator — optimal trim & shoe interval | HorseAppCompare";
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.setAttribute("name", "description"); document.head.appendChild(m); }
    m.setAttribute("content", isNl
      ? "Bereken het ideale interval voor bekappen of beslaan op basis van discipline, ondergrond en hoefkwaliteit. Inclusief checklist voor tussentijdse hoefcontrole."
      : "Calculate the ideal trim or shoeing interval based on discipline, surface and hoof quality. Includes interim hoof check checklist.");
  }, [isNl]);

  const result = useMemo(() => {
    const d = DISCIPLINES.find(x => x.id === discipline);
    const s = SURFACES.find(x => x.id === surface);
    const h = HOOF_QUALITIES.find(x => x.id === hoof);
    let weeks = d.base + s.delta + h.delta;
    if (shod) weeks = Math.max(4, Math.min(weeks, 8));  // shoes typically every 6-8 wks
    else weeks = Math.max(4, Math.min(weeks, 10));  // barefoot can stretch to 10
    return { weeks, nextDate: new Date(Date.now() + weeks * 7 * 24 * 3600 * 1000) };
  }, [discipline, surface, hoof, shod]);

  const warnings = useMemo(() => {
    const items = [];
    if (hoof === "poor") items.push(isNl ? "Bij laminitis-geschiedenis: nooit langer dan 6 weken wachten en altijd in overleg met dierenarts." : "With laminitis history: never wait longer than 6 weeks and always consult vet.");
    if (surface === "rocky" && !shod) items.push(isNl ? "Op rotsachtig terrein zonder beslag: overweeg hoefschoenen tijdens lange ritten." : "On rocky terrain without shoes: consider hoof boots during long rides.");
    if (discipline === "eventing" || discipline === "racing") items.push(isNl ? "Wedstrijdseizoen? Plan bekapping niet binnen 5 dagen voor een wedstrijd (gevoeligheid)." : "Competition season? Don't schedule a trim within 5 days of an event (sensitivity risk).");
    return items;
  }, [hoof, surface, discipline, shod, isNl]);

  return (
    <main data-testid="farrier-page">
      <section className="bg-gradient-to-br from-stone-50 via-white to-white border-b border-[#E2E8F0]">
        <div className="hac-container py-14 max-w-4xl">
          <nav className="flex items-center gap-2 text-xs text-[#64748B] mb-4"><Link to="/" className="hover:text-[#2563EB]">Home</Link><ChevronRight size={12} /><span className="text-[#0F172A] font-medium">{isNl ? "Hoefsmid-planner" : "Farrier planner"}</span></nav>
          <div className="overline mb-2 text-stone-600 inline-flex items-center gap-1.5"><Hammer size={12} /> {isNl ? "Gratis tool" : "Free tool"}</div>
          <h1 className="font-heading text-4xl md:text-6xl text-[#0F172A] leading-[1.05] mb-3">{isNl ? "Hoefsmid-planner" : "Farrier Schedule Calculator"}</h1>
          <p className="text-lg text-[#64748B] leading-relaxed max-w-3xl">{isNl
            ? "Bereken het optimale interval voor bekappen of beslaan, afgestemd op discipline, ondergrond en hoefkwaliteit van je paard."
            : "Calculate the optimal trim or shoeing interval, tailored to your horse's discipline, surface and hoof quality."}</p>
        </div>
      </section>

      <section className="hac-container py-10 max-w-6xl grid lg:grid-cols-2 gap-6">
        <form onSubmit={(e) => e.preventDefault()} className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-5">
          <h2 className="font-bold text-xl text-[#0F172A]">{isNl ? "Invoer" : "Inputs"}</h2>
          <div><label className="overline block mb-1 text-[#64748B]">{isNl ? "Discipline" : "Discipline"}</label><select className="hac-input" value={discipline} onChange={(e) => setDiscipline(e.target.value)} data-testid="farrier-discipline">{DISCIPLINES.map(d => <option key={d.id} value={d.id}>{isNl ? d.nl : d.en}</option>)}</select></div>
          <div><label className="overline block mb-1 text-[#64748B]">{isNl ? "Ondergrond" : "Surface"}</label><select className="hac-input" value={surface} onChange={(e) => setSurface(e.target.value)} data-testid="farrier-surface">{SURFACES.map(s => <option key={s.id} value={s.id}>{isNl ? s.nl : s.en}</option>)}</select></div>
          <div><label className="overline block mb-1 text-[#64748B]">{isNl ? "Hoefkwaliteit" : "Hoof quality"}</label><select className="hac-input" value={hoof} onChange={(e) => setHoof(e.target.value)} data-testid="farrier-hoof">{HOOF_QUALITIES.map(h => <option key={h.id} value={h.id}>{isNl ? h.nl : h.en}</option>)}</select></div>
          <div className="flex items-center gap-3 border-t border-[#E2E8F0] pt-4">
            <input type="checkbox" id="shod" checked={shod} onChange={(e) => setShod(e.target.checked)} className="w-4 h-4" data-testid="farrier-shod" />
            <label htmlFor="shod" className="text-sm text-[#0F172A]">{isNl ? "Paard heeft beslag (i.p.v. blootsvoets)" : "Horse is shod (vs. barefoot)"}</label>
          </div>
        </form>

        <div className="space-y-4">
          <div className="bg-gradient-to-br from-stone-700 to-stone-900 text-white rounded-2xl p-6">
            <div className="text-xs uppercase tracking-widest text-stone-300 mb-2 inline-flex items-center gap-1.5"><Sparkles size={12} /> {isNl ? "Aanbevolen interval" : "Recommended interval"}</div>
            <div className="text-6xl font-bold mb-1 tabular-nums" data-testid="farrier-weeks">{result.weeks} <span className="text-2xl text-stone-300">{isNl ? "weken" : "weeks"}</span></div>
            <div className="text-stone-300 text-sm">{isNl ? "Volgende afspraak rond" : "Next appointment around"} {result.nextDate.toLocaleDateString(isNl ? "nl-NL" : "en-GB", { day: "2-digit", month: "long", year: "numeric" })}</div>
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
            <h3 className="font-bold text-[#0F172A] mb-3 inline-flex items-center gap-2"><CheckCircle2 size={16} className="text-emerald-600" /> {isNl ? "Tussentijdse hoefcheck (wekelijks)" : "Weekly interim hoof check"}</h3>
            <ul className="space-y-1.5 text-sm text-[#0F172A]">
              {(isNl ? [
                "Hoeven uitkrabben: geen losse stukken hoorn of rotte straal",
                "Geen pijnreactie bij druk op zool/kroon",
                "Hoefwand symmetrisch, geen scheuren > 1cm",
                "IJzers stevig vast (bij beslag) — geen losse spijkers",
                "Geen warmte of bonzende slagaders boven kroon (laminitis-teken)",
              ] : [
                "Pick out hooves: no loose horn or rotten frog",
                "No pain response to sole/coronet pressure",
                "Hoof wall symmetric, no cracks > 1cm",
                "Shoes firmly set (if shod) — no loose nails",
                "No heat or throbbing pulse above coronet (laminitis sign)",
              ]).map((item, i) => <li key={i} className="flex gap-2"><span className="text-emerald-500 shrink-0">✓</span>{item}</li>)}
            </ul>
          </div>

          {warnings.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs space-y-2">
              {warnings.map((w, i) => <p key={i} className="text-amber-900 inline-flex items-start gap-2"><AlertTriangle size={14} className="text-amber-600 shrink-0 mt-0.5" />{w}</p>)}
            </div>
          )}

          <AiAnalysisCard tool="farrier" accent="stone" inputs={{
            discipline: DISCIPLINES.find(x => x.id === discipline) ? (isNl ? DISCIPLINES.find(x => x.id === discipline).nl : DISCIPLINES.find(x => x.id === discipline).en) : discipline,
            surface: SURFACES.find(x => x.id === surface) ? (isNl ? SURFACES.find(x => x.id === surface).nl : SURFACES.find(x => x.id === surface).en) : surface,
            hoof: HOOF_QUALITIES.find(x => x.id === hoof) ? (isNl ? HOOF_QUALITIES.find(x => x.id === hoof).nl : HOOF_QUALITIES.find(x => x.id === hoof).en) : hoof,
            shod: shod ? (isNl ? "ja, beslag" : "yes, shod") : (isNl ? "blootsvoets" : "barefoot"),
            weeks: result.weeks,
          }} />
        </div>
      </section>
    </main>
  );
};

export default FarrierScheduleCalculatorPage;
